import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ESTIMATE_STATUS_LABELS,
  formatPhone,
  INVOICE_STATUS_LABELS,
  JOB_STATUS_LABELS,
} from '@fsm/shared';
import { api } from '../lib/api';
import { keys } from '../lib/queries';
import { cx, Spinner } from './ui';

/**
 * One box for everything.
 *
 * Whoever picks up the phone has a name, a number or a street — not a section
 * of the app. Asking them to decide whether the thing they are looking for is
 * a customer, a job, an estimate or an invoice before they are allowed to look
 * for it is the most common way an office tool wastes somebody's time while a
 * customer is waiting on the line.
 *
 * So it sits at the top of the rail, above the first destination: the search
 * comes before choosing where to go, because often it replaces the choice.
 */

export interface SearchHit {
  key: string;
  group: string;
  to: string;
  title: string;
  meta: string;
}

export function GlobalSearch() {
  const navigate = useNavigate();
  const [raw, setRaw] = useState('');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  // Debounced: a dispatcher types a seven-digit number in well under a second
  // and every keystroke is four indexed queries on the server.
  useEffect(() => {
    const timer = setTimeout(() => setTerm(raw.trim()), 180);
    return () => clearTimeout(timer);
  }, [raw]);

  const { data, isFetching } = useQuery({
    queryKey: keys.search(term),
    queryFn: () => api.search(term),
    enabled: term.length >= 2,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  const hits = useMemo<SearchHit[]>(() => {
    if (!data) return [];
    return [
      ...data.customers.map((c) => ({
        key: `c:${c.id}`,
        group: 'Customers',
        to: `/customers/${c.id}`,
        title: c.name,
        meta: [c.phone ? formatPhone(c.phone) : null, c.email].filter(Boolean).join(' · '),
      })),
      ...data.jobs.map((j) => ({
        key: `j:${j.id}`,
        group: 'Jobs',
        to: `/jobs/${j.id}`,
        title: `#${j.number} · ${j.title}`,
        meta: [j.customerName, JOB_STATUS_LABELS[j.status as keyof typeof JOB_STATUS_LABELS]]
          .filter(Boolean)
          .join(' · '),
      })),
      ...data.estimates.map((e) => ({
        key: `e:${e.id}`,
        group: 'Estimates',
        to: `/estimates/${e.id}`,
        title: `#${e.number} · ${e.title}`,
        meta: [
          e.customerName,
          ESTIMATE_STATUS_LABELS[e.status as keyof typeof ESTIMATE_STATUS_LABELS],
        ]
          .filter(Boolean)
          .join(' · '),
      })),
      ...data.invoices.map((i) => ({
        key: `i:${i.id}`,
        group: 'Invoices',
        to: `/invoices/${i.id}`,
        title: `Invoice #${i.number}`,
        meta: [
          i.customerName,
          INVOICE_STATUS_LABELS[i.status as keyof typeof INVOICE_STATUS_LABELS],
        ]
          .filter(Boolean)
          .join(' · '),
      })),
    ];
  }, [data]);

  useEffect(() => setCursor(0), [term]);

  // Close on a click anywhere else; the panel overlaps the work area.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // ⌘K / Ctrl-K, and a bare `/` when you are not already typing somewhere.
  // This is the one control worth a shortcut: it is the way out of every
  // other screen.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(
        (document.activeElement?.tagName ?? '').toUpperCase(),
      );
      const combo = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      const slash = event.key === '/' && !typing && !event.metaKey && !event.ctrlKey;
      if (combo || slash) {
        event.preventDefault();
        input.current?.focus();
        input.current?.select();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  function go(hit: SearchHit) {
    setOpen(false);
    setRaw('');
    setTerm('');
    input.current?.blur();
    navigate(hit.to);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      setOpen(false);
      input.current?.blur();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((c) => Math.min(c + 1, hits.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (event.key === 'Enter' && hits[cursor]) {
      event.preventDefault();
      go(hits[cursor]);
    }
  }

  const searching = term.length >= 2;
  const showPanel = open && searching;

  return (
    <div ref={box} className="relative my-7">
      <div className="relative">
        <svg
          viewBox="0 0 20 20"
          fill="none"
          className="pointer-events-none absolute top-1/2 left-2.5 size-[15px] -translate-y-1/2 text-ink-4"
          aria-hidden
        >
          <path
            d="M9 15.5a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13ZM17.5 17.5l-3.9-3.9"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
        <input
          ref={input}
          type="search"
          value={raw}
          onChange={(e) => {
            setRaw(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search everything"
          aria-label="Search customers, jobs, estimates and invoices"
          className={cx(
            'h-[41px] w-full rounded-[7px] border border-line bg-raised pr-9 pl-[30px] text-[12.5px] text-ink',
            'placeholder:text-ink-4 focus:border-accent focus:bg-surface focus:outline-none',
            '[&::-webkit-search-cancel-button]:hidden',
          )}
        />
        {searching && isFetching ? (
          <Spinner className="absolute top-1/2 right-2.5 size-3.5 -translate-y-1/2 text-ink-4" />
        ) : (
          !raw && (
            /* The shortcut, shown where it is used rather than in a help page. */
            <kbd className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 rounded-[3px] border border-line-2 bg-surface px-[5px] text-[11px] text-ink-4">
              /
            </kbd>
          )
        )}
      </div>

      {showPanel && (
        <SearchPanel
          hits={hits}
          cursor={cursor}
          term={term}
          fetching={isFetching}
          onHover={setCursor}
          onPick={go}
        />
      )}
    </div>
  );
}

/**
 * The results, as their own component so the render harness can put a
 * populated one on screen. A dropdown that only exists inside a focus state is
 * a dropdown nobody ever looks at twice.
 */
export function SearchPanel({
  hits,
  cursor,
  term,
  fetching,
  onHover,
  onPick,
}: {
  hits: SearchHit[];
  cursor: number;
  term: string;
  fetching: boolean;
  onHover: (index: number) => void;
  onPick: (hit: SearchHit) => void;
}) {
  let lastGroup = '';

  return (
    <div className="absolute top-full left-0 z-50 mt-1.5 w-[360px] overflow-hidden rounded-card border border-line bg-surface py-1 text-ink shadow-[0_16px_40px_-12px_rgb(23_43_34/0.28),0_0_0_1px_rgb(23_43_34/0.05)]">
      {hits.length === 0 ? (
        <p className="px-3 py-3 text-[13px] text-ink-4">
          {fetching ? 'Searching\u2026' : `Nothing matches \u201c${term}\u201d`}
        </p>
      ) : (
        <ul className="max-h-[60vh] overflow-y-auto">
          {hits.map((hit, index) => {
            const heading = hit.group === lastGroup ? null : (lastGroup = hit.group);
            return (
              <li key={hit.key}>
                {heading && (
                  <div className="px-3.5 pt-2.5 pb-1 text-[10.5px] font-semibold tracking-[0.12em] text-ink-4 uppercase">
                    {heading}
                  </div>
                )}
                <button
                  type="button"
                  onMouseEnter={() => onHover(index)}
                  onClick={() => onPick(hit)}
                  className={cx(
                    'block w-full px-3.5 py-2 text-left',
                    index === cursor && 'bg-accent-soft',
                  )}
                >
                  <div className="truncate text-[13px] font-medium">{hit.title}</div>
                  {hit.meta && <div className="truncate text-[12px] text-ink-4">{hit.meta}</div>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
